import { useCallback } from "react";

import { type SetMyProfileImageDataInput } from "~/gql/graphqlBase.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import { useClearMyProfileImageMutation, useSetMyProfileImageMutation } from "~/services/endpoints/apiEndpoints";

const ENTITY_LABEL = "Profile picture";

export interface UseProfileImageMutationsReturn {
	clearImage: () => Promise<boolean>;
	isMutating: boolean;
	setImage: (input: SetMyProfileImageDataInput) => Promise<boolean>;
}

const useProfileImageMutations = (): UseProfileImageMutationsReturn => {
	const { run } = useMutationRunner();
	const [setMutation, { isLoading: isSetting }] = useSetMyProfileImageMutation();
	const [clearMutation, { isLoading: isClearing }] = useClearMyProfileImageMutation();

	const setImage = useCallback(
		(input: SetMyProfileImageDataInput): Promise<boolean> =>
			run(setMutation({ input }), (data) => data.setMyProfileImage, snackMessages.saveError(ENTITY_LABEL), {
				successMessage: snackMessages.saveSuccess(ENTITY_LABEL)
			}),
		[run, setMutation]
	);

	const clearImage = useCallback(
		(): Promise<boolean> =>
			run(clearMutation(), (data) => data.clearMyProfileImage, snackMessages.removeError(ENTITY_LABEL), {
				successMessage: snackMessages.removeSuccess(ENTITY_LABEL)
			}),
		[clearMutation, run]
	);

	return { clearImage, isMutating: isSetting || isClearing, setImage };
};

export default useProfileImageMutations;
